import { useCallback, useEffect, useState } from 'react'
import { getOrgSettings, saveAutomationMode, getAutomationActivity } from '../api/orgSettings'
import type { AutomationEntry, AutomationMode } from '../types'
import { Linked } from '../components/Linked'

const CHOICES: Array<[AutomationMode, string, string]> = [
  ['off', 'Off', 'Nothing happens automatically.'],
  ['draft', 'Post a private comment', 'Only agents see it. It can be deleted.'],
  ['send', 'Email the customer', 'Sent with nobody reading it first. There is no undo.'],
]

/**
 * Automation, and the one screen in the app where the wrong choice reaches a
 * customer unreviewed. The wording is blunt on purpose.
 */
export function AutomationSection({ compact }: { compact?: boolean }) {
  const [mode, setMode] = useState<AutomationMode>('off')
  const [saved, setSaved] = useState<AutomationMode>('off')
  const [activity, setActivity] = useState<AutomationEntry[]>([])
  const [webhookUrl, setWebhookUrl] = useState('')
  const [copied, setCopied] = useState(false)
  const [copyFailed, setCopyFailed] = useState(false)

  /**
   * Copy the address, by whichever route this browser allows.
   *
   * navigator.clipboard needs the `clipboard-write` permission, and a
   * cross-origin iframe does not get it unless the host grants it — which Desk
   * does not. So the old execCommand path is tried next; it works from inside
   * the frame because it copies the page's own selection. If both refuse, the
   * text is left selected so ctrl-C finishes the job, which is more use than
   * an error telling the agent to do exactly that.
   */
  async function copyAddress() {
    setCopyFailed(false)
    try {
      await navigator.clipboard.writeText(webhookUrl)
      setCopied(true)
      return
    } catch {
      // Fall through to the older route.
    }

    try {
      const box = document.createElement('textarea')
      box.value = webhookUrl
      box.style.position = 'fixed'
      box.style.opacity = '0'
      document.body.appendChild(box)
      box.select()
      const ok = document.execCommand('copy')
      box.remove()
      if (ok) {
        setCopied(true)
        return
      }
    } catch {
      // Nothing left to try but the selection below.
    }

    const shown = document.getElementById('webhook-address')
    if (shown) {
      const range = document.createRange()
      range.selectNodeContents(shown)
      const sel = window.getSelection()
      sel?.removeAllRanges()
      sel?.addRange(range)
    }
    setCopyFailed(true)
  }
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const s = await getOrgSettings()
      setMode(s.automation.mode)
      setSaved(s.automation.mode)
      setWebhookUrl(s.webhookUrl)
      setActivity((await getAutomationActivity()).activity)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function save() {
    setBusy(true)
    setError('')
    try {
      await saveAutomationMode(mode)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const chosen = CHOICES.find(([m]) => m === mode)

  return (
    <div className="card">
      <h2>Automation {saved !== 'off' && <span className="ok">on</span>}</h2>
      <p className="hint">Automatically writes a note or a reply on every new ticket.</p>

      <label className="field">
        <span>When a message arrives</span>
        <select value={mode} onChange={(e) => setMode(e.target.value as AutomationMode)}>
          {CHOICES.map(([m, label]) => (
            <option key={m} value={m}>
              {label}
              {/* Which one is actually running. Picking a mode and not pressing
                  Save leaves the box showing something the org is not doing. */}
              {m === saved ? ' (in use)' : ''}
            </option>
          ))}
        </select>
      </label>

      {/* The consequence of the choice being made, right now. This is the one
          setting in the app that can reach a customer with nobody reading it
          first, so it is the one explanation that stays on the screen. */}
      {chosen && <p className="note">{chosen[2]}</p>}

      {/* The address replies are sent from used to live here, shown only when
          the mode was "email the customer" — but sending by hand needs it too.
          It is under Reply instructions now, with the rest of what every
          outgoing reply looks like. */}
      {mode === 'send' && (
        <p className="note">
          Replies go out from the address the customer wrote to. Set a fallback under
          Reply instructions for tickets raised by hand.
        </p>
      )}

      <button disabled={busy} onClick={save}>
        {busy ? 'Saving…' : 'Save'}
      </button>

      {error && <p className="inline-error"><Linked text={error} /></p>}

      {webhookUrl && (
        <>
          <hr />

          <label className="field">
            <span>
              Paste this into Zoho{' '}
              <span className="note">Setup → Developer Space → Webhooks</span>
            </span>
            <code className="webhook-url" id="webhook-address">
              {webhookUrl}
            </code>
          </label>

          <button className="secondary" onClick={() => void copyAddress()}>
            {copied ? 'Copied' : copyFailed ? 'Select it above and copy' : 'Copy address'}
          </button>

          <p className="note">Keep it private. Anyone with it can reply to your tickets.</p>
        </>
      )}

      {!compact && activity.length > 0 && (
        <>
          <hr />
          <h3 className="group-name">Recent</h3>
          <ul className="docs">
            {activity.map((a, i) => (
              <li key={`${a.at}-${i}`}>
                <div className="doc-lines">
                  <span className="doc-name">
                    <span className="tag">{a.action}</span> #{a.ticketId}
                  </span>
                  <span className="doc-url">{a.reason ?? a.sources.join(', ')}</span>
                </div>
                <span className="note">{a.at.slice(5, 16).replace('T', ' ')}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
